import { useEffect, useMemo, useState } from "react";
import { collectionsListName, departmentsListName, dependenciesListName, MDWorkstationListName } from "src/api/config";
import { TGDisclosure } from "src/components/ui/TGDisclosure";
import { useAppDispatch } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { getItems } from "src/modules/spItems/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { TGListWithSearch } from "src/pages/DashboardPage/acadia/components/Filters/ListsSearch";
import { SubResetFilter } from "src/pages/DashboardPage/mytasks/meetingsPage/components/MeetingCard";
import { FilterUser } from "src/pages/DashboardPage/mytasks/reports/ActionsReport/ActionsReport";
import { SubmitWorkPlaceFiltersType } from "src/pages/Reports/types";
import { CheckBoxListItem, UserListItem } from "src/types/main";
import { chunkArray } from "src/utils/array.utils";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";

type FirstFilter = "collections" | "departments" | "workstations" | "employees" | null;

// Runtime shape of MDWorkstationListName items after selectItems processing
type ProcessedWorkstation = {
  ID: number;
  Workstation: string;
  Location?: { ID: number; Title: string } | null;
  Department?: { ID: number; Title: string } | null;
  User?: Array<{ Name: string; ID: number; Email: string }> | false | null;
};

function Filters({
  submitFilters,
  changeSubmitFilters,
  resetSubmitFilters,
  isFiltersEmpty,
  firstSelectedFilter,
  changeFirstSelectedFilter,
}: {
  submitFilters: SubmitWorkPlaceFiltersType;
  changeSubmitFilters: (e: SubmitWorkPlaceFiltersType) => void;
  resetSubmitFilters: () => void;
  isFiltersEmpty: boolean;
  firstSelectedFilter: FirstFilter;
  changeFirstSelectedFilter: (value: FirstFilter) => void;
}) {
  const { collections, departments, workstations, employees } = submitFilters;

  const dispatch = useAppDispatch();
  const { data: collectionsResponseData, status: collectionsStatus } = useGetSelect(collectionsListName);
  const { data: departmentsResponseData, status: departmentsStatus } = useGetSelect(departmentsListName);
  const { data: workstationsResponseData, status: workstationsStatus } = useGetSelect(MDWorkstationListName);

  const wsData = workstationsResponseData as unknown as ProcessedWorkstation[];

  const [collectionOptions, setCollectionOptions] = useState<CheckBoxListItem[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<CheckBoxListItem[]>([]);
  const [workstationOptions, setWorkstationOptions] = useState<CheckBoxListItem[]>([]);
  const [validUserEmails, setValidUserEmails] = useState<Set<string> | null>(null);
  const [dependLoading, setDependLoading] = useState<boolean>(false);
  const [filterLoading, setFilterLoading] = useState<boolean>(false);
  const [filterUserKey, setFilterUserKey] = useState(0);

  function checkListItem(key: keyof SubmitWorkPlaceFiltersType, value: CheckBoxListItem) {
    const values = submitFilters[key] as CheckBoxListItem[];

    let newValue = [...values];

    if (newValue.length > 0 && newValue.find((f) => f.id === value.id)) {
      const filtered = values.filter((n) => n.id !== value.id);

      newValue = filtered;
    } else {
      newValue.push(value);
    }

    changeSubmitFilters({ ...submitFilters, [key]: newValue });
  }

  function checkListUserItem(key: keyof SubmitWorkPlaceFiltersType, value: UserListItem) {
    const values = submitFilters[key] as UserListItem[];

    let newValue = [...values];
    if (newValue.length > 0 && newValue.find((f) => f.id === value.id)) {
      const filtered = values.filter((n: any) => n.id !== value.id);
      newValue = filtered;
    } else {
      newValue.push(value);
    }
    changeSubmitFilters({ ...submitFilters, [key]: newValue });
  }

  const resetFilters = () => {
    changeFirstSelectedFilter(null);
    setValidUserEmails(null);
    setCollectionOptions(collectionOptions.map((f) => ({ ...f, disabled: false })));
    setDepartmentOptions(departmentOptions.map((f) => ({ ...f, disabled: false })));
    setWorkstationOptions(workstationOptions.map((f) => ({ ...f, disabled: false })));
  };

  function clearAll() {
    resetSubmitFilters();
    resetFilters();
    setFilterUserKey((prev) => prev + 1);
  }

  function reset(key: keyof typeof submitFilters, value: any) {
    changeSubmitFilters({ ...submitFilters, [key]: value });
  }

  const attributesFiltersCount = useMemo(() => {
    const count =
      Number(collections.length > 0) +
      Number(departments.length > 0) +
      Number(workstations.length > 0) +
      Number(employees.length > 0);

    return count;
  }, [JSON.stringify(submitFilters)]);

  // Определяет firstSelectedFilter: какой фильтр был выбран первым
  useEffect(() => {
    const mainFilters = {
      collections: submitFilters.collections,
      departments: submitFilters.departments,
      workstations: submitFilters.workstations,
      employees: submitFilters.employees,
    };

    const selectedFilters = Object.entries(mainFilters).filter(([, value]) => value.length > 0);

    if (selectedFilters.length === 0) {
      return resetFilters();
    }

    setDependLoading(true);
    setFilterLoading(true);

    const key = selectedFilters[0][0] as keyof typeof mainFilters;

    if (selectedFilters.length === 1 && firstSelectedFilter === null) {
      return changeFirstSelectedFilter(key as FirstFilter);
    } else if (selectedFilters.length === 1 && key !== firstSelectedFilter && firstSelectedFilter !== null) {
      return changeFirstSelectedFilter(key as FirstFilter);
    }

    setDependLoading(false);
    setFilterLoading(false);
  }, [
    submitFilters.collections.length,
    submitFilters.departments.length,
    submitFilters.workstations.length,
    submitFilters.employees.length,
  ]);

  // Основная логика дизейбла: все 4 фильтра зависят друг от друга
  useAsyncEffect(async () => {
    const hasCollections = submitFilters.collections.length > 0;
    const hasDepartments = submitFilters.departments.length > 0;
    const hasWorkstations = submitFilters.workstations.length > 0;
    const hasEmployees = submitFilters.employees.length > 0;

    if (!hasCollections && !hasDepartments && !hasWorkstations && !hasEmployees) return;

    try {
      setFilterLoading(true);
      setDependLoading(true);

      if (firstSelectedFilter === "employees" && hasEmployees) {
        // Первым выбрали сотрудника.
        // Находим рабочие места, в User которых есть хотя бы один выбранный сотрудник.
        // Относительно этих рабочих мест дизейблим коллекции и подразделения.
        const selectedEmails = new Set(
          submitFilters.employees.map((e) => e.email?.toLowerCase()).filter(Boolean) as string[],
        );

        const validWs = wsData.filter((ws) => {
          const users = Array.isArray(ws.User) ? ws.User : [];
          return users.some((u) => u.Email && selectedEmails.has(u.Email.toLowerCase()));
        });

        const validWorkstationIds = new Set(validWs.map((ws) => ws.ID));
        const validCollectionIds = new Set(
          validWs.map((ws) => ws.Location?.ID).filter((id): id is number => id != null),
        );
        const validDeptIds = new Set(validWs.map((ws) => ws.Department?.ID).filter((id): id is number => id != null));

        setCollectionOptions(
          collectionsResponseData.map((loc) => ({
            id: loc.ID,
            title: loc.Title,
            rest: loc,
            disabled: !validCollectionIds.has(loc.ID),
          })),
        );

        setDepartmentOptions(
          departmentsResponseData.map((dep) => ({
            id: dep.ID,
            title: dep.Title,
            rest: dep,
            disabled: !validDeptIds.has(dep.ID),
          })),
        );

        setWorkstationOptions(
          workstationsResponseData.map((ws) => ({
            id: (ws as unknown as ProcessedWorkstation).ID,
            title: (ws as unknown as ProcessedWorkstation).Workstation,
            rest: ws,
            disabled: !validWorkstationIds.has((ws as unknown as ProcessedWorkstation).ID),
          })),
        );

        setValidUserEmails(null);
      } else if (firstSelectedFilter === "workstations" && hasWorkstations) {
        // Первым выбрали рабочее место.
        // Относительно него дизейблим коллекции, подразделения и фильтруем сотрудников.
        const selectedWsIds = new Set(submitFilters.workstations.map((w) => Number(w.id)));

        const validWs = wsData.filter((ws) => selectedWsIds.has(ws.ID));

        const validCollectionIds = new Set(
          validWs.map((ws) => ws.Location?.ID).filter((id): id is number => id != null),
        );
        const validDeptIds = new Set(validWs.map((ws) => ws.Department?.ID).filter((id): id is number => id != null));
        const validEmails = new Set(
          validWs.flatMap((ws) =>
            Array.isArray(ws.User) ? ws.User.map((u) => u.Email).filter((e): e is string => Boolean(e)) : [],
          ),
        );

        setCollectionOptions(
          collectionsResponseData.map((loc) => ({
            id: loc.ID,
            title: loc.Title,
            rest: loc,
            disabled: !validCollectionIds.has(loc.ID),
          })),
        );

        setDepartmentOptions(
          departmentsResponseData.map((dep) => ({
            id: dep.ID,
            title: dep.Title,
            rest: dep,
            disabled: !validDeptIds.has(dep.ID),
          })),
        );

        setWorkstationOptions(
          workstationsResponseData.map((ws) => ({
            id: (ws as unknown as ProcessedWorkstation).ID,
            title: (ws as unknown as ProcessedWorkstation).Workstation,
            rest: ws,
            disabled: false,
          })),
        );

        setValidUserEmails(validEmails.size > 0 ? validEmails : null);
      } else if (hasCollections || hasDepartments) {
        // Первым выбрали коллекцию или подразделение.
        // 1) dependenciesListName — взаимозависимость коллекций ↔ подразделений.
        // 2) workstationsResponseData (клиент-сайд) — допустимые рабочие места и сотрудники.
        const selected = {
          collections: submitFilters.collections.map((l) => Number(l.id)),
          departments: submitFilters.departments.map((d) => Number(d.id)),
        };

        // Use only the primary filter for the dependency query so that secondary-level
        // options (e.g. all departments valid for Location1) remain visible for multi-select.
        // Both filters together would narrow to an intersection, hiding still-valid options.
        const primaryDepFilters: Partial<{ Collection: number[]; Department: number[] }> = {};
        if (firstSelectedFilter === "collections" && selected.collections.length > 0) {
          primaryDepFilters.Collection = selected.collections;
        } else if (firstSelectedFilter === "departments" && selected.departments.length > 0) {
          primaryDepFilters.Department = selected.departments;
        } else {
          if (selected.collections.length > 0) primaryDepFilters.Collection = selected.collections;
          if (selected.departments.length > 0) primaryDepFilters.Department = selected.departments;
        }

        const { items: dependItems } = await getItemsRequest({
          listName: dependenciesListName,
          filters: primaryDepFilters,
        });

        const depResultData = {
          collections: new Set<number>(),
          departments: new Set<number>(),
        };

        if (dependItems.length > 0) {
          const chunks = chunkArray(dependItems, 25);

          const promises = chunks.map(async (chunk) => {
            const local = {
              collections: new Set<number>(),
              departments: new Set<number>(),
            };

            const modifiedItems = modifiedItemsState(chunk, dependenciesListName);

            modifiedItems.reduce((acc, item) => {
              acc.collections.add(item.Collection.ID);
              acc.departments.add(item.Department.ID);
              return acc;
            }, local);

            return local;
          });

          const results = await Promise.all(promises);

          results.forEach((r) => {
            r.collections.forEach((id) => depResultData.collections.add(id));
            r.departments.forEach((id) => depResultData.departments.add(id));
          });
        }

        // Клиент-сайд: фильтруем рабочие места и получаем допустимых сотрудников
        const validWs = wsData.filter((ws) => {
          const matchCollection =
            selected.collections.length === 0 ||
            (ws.Location?.ID != null && selected.collections.includes(ws.Location.ID));
          const matchDept =
            selected.departments.length === 0 ||
            (ws.Department?.ID != null && selected.departments.includes(ws.Department.ID));
          return matchCollection && matchDept;
        });

        const validWorkstationIds = new Set(validWs.map((ws) => ws.ID));
        const validEmails = new Set(
          validWs.flatMap((ws) =>
            Array.isArray(ws.User) ? ws.User.map((u) => u.Email).filter((e): e is string => Boolean(e)) : [],
          ),
        );

        setCollectionOptions(
          collectionsResponseData.map((loc) => ({
            id: loc.ID,
            title: loc.Title,
            rest: loc,
            disabled:
              firstSelectedFilter === null
                ? false
                : firstSelectedFilter !== "collections" && !depResultData.collections.has(loc.ID),
          })),
        );

        setDepartmentOptions(
          departmentsResponseData.map((dep) => ({
            id: dep.ID,
            title: dep.Title,
            rest: dep,
            disabled:
              firstSelectedFilter === null
                ? false
                : firstSelectedFilter !== "departments" && !depResultData.departments.has(dep.ID),
          })),
        );

        setWorkstationOptions(
          workstationsResponseData.map((ws) => ({
            id: (ws as unknown as ProcessedWorkstation).ID,
            title: (ws as unknown as ProcessedWorkstation).Workstation,
            rest: ws,
            disabled:
              firstSelectedFilter === null
                ? false
                : !validWorkstationIds.has((ws as unknown as ProcessedWorkstation).ID),
          })),
        );

        setValidUserEmails(validEmails.size > 0 ? validEmails : null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFilterLoading(false);
      setDependLoading(false);
    }
  }, [
    collectionsResponseData,
    departmentsResponseData,
    workstationsResponseData,
    submitFilters.collections,
    submitFilters.departments,
    submitFilters.workstations,
    submitFilters.employees,
    firstSelectedFilter,
    collectionsStatus,
    departmentsStatus,
    workstationsStatus,
  ]);

  // Инициализация опций при первой загрузке списков
  useEffect(() => {
    if (collectionsStatus === "SUCCESS") {
      setCollectionOptions(collectionsResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (departmentsStatus === "SUCCESS") {
      setDepartmentOptions(departmentsResponseData.map((f) => ({ id: f.ID, title: f.Title, rest: f })));
    }

    if (workstationsStatus === "SUCCESS") {
      setWorkstationOptions(
        workstationsResponseData.map((f) => ({
          id: (f as unknown as ProcessedWorkstation).ID,
          title: (f as unknown as ProcessedWorkstation).Workstation,
          rest: f,
        })),
      );
    }
  }, [
    collectionsResponseData,
    departmentsResponseData,
    workstationsResponseData,
    collectionsStatus,
    departmentsStatus,
    workstationsStatus,
  ]);

  useEffect(() => {
    dispatch(getItems({ listName: collectionsListName }));
    dispatch(getItems({ listName: departmentsListName }));
    dispatch(getItems({ listName: MDWorkstationListName }));
  }, []);

  return (
    <FlexCol className="overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-full">
      {!isFiltersEmpty && (
        <FlexRow className="flex-nowrap gap-x-2 items-center p-5 border-b cursor-pointer group" onClick={clearAll}>
          <FlexCol className="group-hover:underline">Сбросить все фильтры</FlexCol>
          <FlexCol>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_381_6476)">
                <path
                  d="M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z"
                  fill="#637381"
                />
              </g>
              <defs>
                <clipPath id="clip0_381_6476">
                  <rect width="16" height="16" fill="white" transform="translate(0 16) rotate(-90)" />
                </clipPath>
              </defs>
            </svg>
          </FlexCol>
        </FlexRow>
      )}
      <TGDisclosure
        defaultOpen={true}
        title="Атрибуты рабочего места"
        count={attributesFiltersCount}
        panelClassName="px-0"
      >
        <TGDisclosure
          title="Коллекция"
          count={collections.length}
          disabled={filterLoading || dependLoading || collectionsStatus === "LOADING"}
        >
          <TGListWithSearch
            listData={collectionOptions}
            items={collections}
            checkedItems={(value) => checkListItem("collections", value)}
          />
          <SubResetFilter resetFilter={() => reset("collections", [])} />
        </TGDisclosure>
        <TGDisclosure
          title="Подразделение/Функция"
          count={departments.length}
          disabled={filterLoading || dependLoading || departmentsStatus === "LOADING"}
        >
          <TGListWithSearch
            listData={departmentOptions}
            items={departments}
            checkedItems={(value) => checkListItem("departments", value)}
          />
          <SubResetFilter resetFilter={() => reset("departments", [])} />
        </TGDisclosure>
        <TGDisclosure
          title="Рабочее место"
          count={workstations.length}
          disabled={filterLoading || dependLoading || workstationsStatus === "LOADING"}
        >
          <TGListWithSearch
            listData={workstationOptions}
            items={workstations}
            checkedItems={(value) => checkListItem("workstations", value)}
          />
          <SubResetFilter resetFilter={() => reset("workstations", [])} />
        </TGDisclosure>
        <TGDisclosure title="ФИО сотрудника" count={employees.length} wrapperClassName="border-b-0">
          <FilterUser
            key={filterUserKey}
            selected={employees}
            checkEmployees={(value) => checkListUserItem("employees", value)}
            reset={() => reset("employees", [])}
            allowedEmails={validUserEmails}
          />
        </TGDisclosure>
      </TGDisclosure>
    </FlexCol>
  );
}

export default Filters;
